import { useRouter } from 'next/router'

import { type FC, memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Checkbox } from 'components/Checkbox'
import { TabPanel } from 'components/Tab'
import {
	selectPerpsProvider,
	selectUserInfoCollapsed,
	selectUserInfoShowAllMarkets,
} from 'state/futures/common/selectors'
import { setPreferences } from 'state/futures/reducer'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import { DRAG_HANDLE_CLASS_NAME } from 'components/GridLayout'
import AssetsTable from './AssetsTable'
import ConditionalOrdersTable from './ConditionalOrdersTable'
import OrderHistoryTable from './OrderHistoryTable'
import PositionsTable from './PositionsTable'
import TradesTable from './TradesTable'
import TransactionHistoryTable from './TransactionHistoryTable'
import UserInfoTabs, { FutureTabs, FuturesTab } from './UserInfoTabs'

const UserInfo: FC = memo(() => {
	const { t } = useTranslation()
	const router = useRouter()
	const dispatch = useAppDispatch()

	const perpsProvider = useAppSelector(selectPerpsProvider)
	const userInfoCollapsed = useAppSelector(selectUserInfoCollapsed)
	const userInfoShowAllMarkets = useAppSelector(selectUserInfoShowAllMarkets)

	const activeTab = useMemo(() => {
		if (router.query.tab) {
			const tab = router.query.tab as FuturesTab
			if (
				FutureTabs.includes(tab) &&
				(providerIsCrossMargin(perpsProvider) || tab !== FuturesTab.ASSETS)
			) {
				return tab
			}
		}
		return FuturesTab.POSITION
	}, [router, perpsProvider])

	const handleToggleShowAllMarkets = useCallback(
		() => dispatch(setPreferences({ userInfoShowAllMarkets: !userInfoShowAllMarkets })),
		[dispatch, userInfoShowAllMarkets]
	)

	return (
		<UserInfoContainer>
			<TabButtonsContainer>
				<UserInfoTabs activeTab={activeTab} />
				<TabCenter className={DRAG_HANDLE_CLASS_NAME} />
				<TabRight>
					<Checkbox
						id="show-all-markets"
						onChange={handleToggleShowAllMarkets}
						checked={userInfoShowAllMarkets}
						label={t('futures.market.user.all-markets')}
						variant="simple"
					/>
				</TabRight>
			</TabButtonsContainer>

			<CollapsibleTabPanel
				collapsed={userInfoCollapsed}
				name={FuturesTab.POSITION}
				activeTab={activeTab}
				fullHeight
			>
				<PositionsTable />
			</CollapsibleTabPanel>
			<CollapsibleTabPanel
				collapsed={userInfoCollapsed}
				name={FuturesTab.CONDITIONAL_ORDERS}
				activeTab={activeTab}
				fullHeight
			>
				<ConditionalOrdersTable primaryBackground={false} />
			</CollapsibleTabPanel>
			<CollapsibleTabPanel
				collapsed={userInfoCollapsed}
				name={FuturesTab.TRANSACTION_HISTORY}
				activeTab={activeTab}
				fullHeight
			>
				<TransactionHistoryTable />
			</CollapsibleTabPanel>
			<CollapsibleTabPanel
				collapsed={userInfoCollapsed}
				name={FuturesTab.ORDER_HISTORY}
				activeTab={activeTab}
				fullHeight
			>
				<OrderHistoryTable />
			</CollapsibleTabPanel>
			<CollapsibleTabPanel
				collapsed={userInfoCollapsed}
				name={FuturesTab.TRADES}
				activeTab={activeTab}
				fullHeight
			>
				<TradesTable />
			</CollapsibleTabPanel>
			{providerIsCrossMargin(perpsProvider) && (
				<CollapsibleTabPanel
					collapsed={userInfoCollapsed}
					name={FuturesTab.ASSETS}
					activeTab={activeTab}
					fullHeight
				>
					<AssetsTable />
				</CollapsibleTabPanel>
			)}
		</UserInfoContainer>
	)
})

const UserInfoContainer = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
	overflow: hidden;
	border-top: ${(props) => props.theme.colors.selectedTheme.border};
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 40px;
	gap: 15px;

	padding-left: 6px;

	button {
		font-size: 13px;
	}
`

const TabCenter = styled.div`
	flex: 1;
`

const TabRight = styled.div`
	display: flex;
	justify-content: flex-end;
	align-items: center;
	min-width: 150px;
	padding-right: 10px;
`

const CollapsibleTabPanel = styled(TabPanel).attrs({ fade: true })<{ collapsed: boolean }>`
	display: flex;
	flex-direction: column;
	flex: ${({ collapsed }) => (collapsed ? '0' : 'auto')};
	transition: flex 0.4s ease-in-out;
`

export default UserInfo
